/**
 * Unit-system-aware display formatters.
 *
 * All values come from DB in canonical metric (km, kg, m, °C, L).
 * These formatters convert + format for display based on tenant unitSystem.
 *
 * @see docs/planning/turkey-localization/03-units-and-measurements.md §3.5
 */

import type { UnitSystem } from './country-config.types';
import { UnitConverter } from './unit-converter';

/**
 * Format a canonical km value for display.
 * Imperial: converts to miles, shows "mi". Metric: shows "km".
 */
export function formatDistance(
  value: number | undefined | null,
  unitSystem: UnitSystem,
  decimals = 1
): string {
  if (value === undefined || value === null || value <= 0) return '-';
  if (unitSystem === 'imperial') {
    const miles = UnitConverter.kmToMiles(value);
    return `${miles.toLocaleString(undefined, { maximumFractionDigits: decimals })} mi`;
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: decimals })} km`;
}

/**
 * Format a canonical kg value for display.
 * Imperial: converts to lbs, shows "lbs". Metric: shows "kg".
 */
export function formatWeight(
  value: number | undefined | null,
  unitSystem: UnitSystem,
  decimals = 1
): string {
  if (value === undefined || value === null || value < 0) return '-';
  if (unitSystem === 'imperial') {
    const lbs = UnitConverter.kgToLbs(value);
    return `${lbs.toLocaleString(undefined, { maximumFractionDigits: decimals })} lbs`;
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: decimals })} kg`;
}

/**
 * Format a canonical m³ value for display.
 * Imperial: converts to ft³, shows "ft³". Metric: shows "m³".
 */
export function formatVolume(
  value: number | undefined | null,
  unitSystem: UnitSystem,
  decimals = 2
): string {
  if (value === undefined || value === null || value < 0) return '-';
  if (unitSystem === 'imperial') {
    const cuft = UnitConverter.m3ToCuft(value);
    return `${cuft.toLocaleString(undefined, { maximumFractionDigits: decimals })} ft³`;
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: decimals })} m³`;
}

/**
 * Format a canonical °C value for display.
 * Imperial: converts to °F. Metric: shows °C.
 */
export function formatTemperature(
  value: number | undefined | null,
  unitSystem: UnitSystem,
  decimals = 1
): string {
  if (value === undefined || value === null) return '-';
  if (unitSystem === 'imperial') {
    const f = UnitConverter.celsiusToFahrenheit(value);
    return `${f.toFixed(decimals)} °F`;
  }
  return `${value.toFixed(decimals)} °C`;
}

/**
 * Format a canonical km/h value for display.
 * Imperial: converts to mph. Metric: shows km/h.
 */
export function formatSpeed(
  value: number | undefined | null,
  unitSystem: UnitSystem,
  decimals = 0
): string {
  if (value === undefined || value === null || value < 0) return '-';
  if (unitSystem === 'imperial') {
    const mph = UnitConverter.kmhToMph(value);
    return `${mph.toFixed(decimals)} mph`;
  }
  return `${value.toFixed(decimals)} km/h`;
}

/**
 * Format a canonical liters value for display.
 * Imperial: converts to gallons, shows "gal". Metric: shows "L".
 */
export function formatFuel(
  value: number | undefined | null,
  unitSystem: UnitSystem,
  decimals = 1
): string {
  if (value === undefined || value === null || value < 0) return '-';
  if (unitSystem === 'imperial') {
    const gal = UnitConverter.litersToGallons(value);
    return `${gal.toLocaleString(undefined, { maximumFractionDigits: decimals })} gal`;
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: decimals })} L`;
}

/**
 * Format a canonical meters value as length for display.
 * Imperial: converts to feet, shows "ft". Metric: shows "m".
 */
export function formatLength(
  value: number | undefined | null,
  unitSystem: UnitSystem,
  decimals = 2
): string {
  if (value === undefined || value === null || value < 0) return '-';
  if (unitSystem === 'imperial') {
    const ft = UnitConverter.metersToFeet(value);
    return `${ft.toLocaleString(undefined, { maximumFractionDigits: decimals })} ft`;
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: decimals })} m`;
}
